<template>
  <section class="gallery" id="gallery">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">Notre galerie</h2>
        <p class="section-subtitle">Découvrez notre équipe et nos installations</p>
      </div>

      <div class="gallery-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="gallery-grid">
        <div
          v-for="(image, index) in filteredImages"
          :key="index"
          class="gallery-item"
          @click="openLightbox(index)"
        >
          <img :src="image.src" :alt="image.alt" loading="lazy" />
          <div class="gallery-overlay">
            <span class="overlay-icon">🔍</span>
            <p class="overlay-text">{{ image.caption }}</p>
          </div>
        </div>
      </div>

      <!-- Lightbox -->
      <transition name="fade">
        <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
          <button class="lightbox-close" @click.stop="closeLightbox">×</button>
          <button class="lightbox-prev" @click.stop="prevImage">‹</button>
          <button class="lightbox-next" @click.stop="nextImage">›</button>

          <div class="lightbox-content" @click.stop>
            <img
              :src="filteredImages[currentImageIndex]?.src"
              :alt="filteredImages[currentImageIndex]?.alt"
            />
            <p class="lightbox-caption">{{ filteredImages[currentImageIndex]?.caption }}</p>
          </div>
        </div>
      </transition>
    </div>
  </section>
</template>

<script>
export default {
  name: 'GallerySection',
  data() {
    return {
      activeTab: 'all',
      lightboxOpen: false,
      currentImageIndex: 0,
      tabs: [
        { id: 'all', label: 'Tout' },
        { id: 'team', label: 'Notre équipe' },
        { id: 'office', label: 'Nos locaux' },
        { id: 'events', label: 'Événements' }
      ],
      images: [
        {
          src: '/tofs_1.jpg',
          alt: 'Équipe Oasis Finance',
          caption: 'Notre équipe dévouée à votre service',
          category: 'team'
        },
        {
          src: '/tofs_2.jpg',
          alt: 'Équipe au bureau',
          caption: 'Réunion d\'équipe hebdomadaire',
          category: 'team'
        },
        {
          src: '/tofs_3.jpg',
          alt: 'Personnel Oasis',
          caption: 'L\'équipe au complet',
          category: 'team'
        },
        {
          src: '/tofs_4.jpg',
          alt: 'Staff Oasis Finance',
          caption: 'Nos conseillers financiers',
          category: 'team'
        },
        {
          src: '/tofs_5.jpg',
          alt: 'Façade Oasis Finance',
          caption: 'Notre agence à TONGO, Ngaoundéré',
          category: 'office'
        },
        {
          src: '/tofs_6.jpg',
          alt: 'Entrée Oasis',
          caption: 'Bienvenue chez Oasis Microfinance',
          category: 'office'
        },
        {
          src: '/accueil2.jpg',
          alt: 'Espace accueil',
          caption: 'Notre espace d\'accueil chaleureux',
          category: 'office'
        },
        {
          src: '/accueil3.jpg',
          alt: 'Service client',
          caption: 'Service client en action',
          category: 'office'
        },
        {
          src: '/acceuil.jpg',
          alt: 'Bureau service',
          caption: 'Conseils personnalisés pour nos clients',
          category: 'office'
        }
      ]
    }
  },
  computed: {
    filteredImages() {
      if (this.activeTab === 'all') {
        return this.images
      }
      return this.images.filter(img => img.category === this.activeTab)
    }
  },
  methods: {
    openLightbox(index) {
      this.currentImageIndex = index
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeLightbox() {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    },
    nextImage() {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.filteredImages.length
    },
    prevImage() {
      this.currentImageIndex = (this.currentImageIndex - 1 + this.filteredImages.length) % this.filteredImages.length
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.gallery {
  padding: 6rem 1rem;
  background: #fff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  color: #0a2e38;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #1cc48d, #0f4c75);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #5a6f7b;
  margin-top: 1.5rem;
}

.gallery-tabs {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.tab-button {
  padding: 0.8rem 2rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  color: #5a6f7b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button:hover {
  border-color: #1cc48d;
  color: #1cc48d;
  transform: translateY(-2px);
}

.tab-button.active {
  background: linear-gradient(135deg, #1cc48d, #17a97a);
  border-color: #1cc48d;
  color: white;
  box-shadow: 0 5px 15px rgba(28, 196, 141, 0.3);
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.gallery-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 4/3;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.gallery-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.2);
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.gallery-item:hover img {
  transform: scale(1.1);
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(10, 46, 56, 0.9), transparent 50%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.overlay-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.overlay-text {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
}

.lightbox-content {
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 50px rgba(0,0,0,0.5);
}

.lightbox-caption {
  color: white;
  font-size: 1.2rem;
  margin-top: 1.5rem;
  text-align: center;
}

.lightbox-close,
.lightbox-prev,
.lightbox-next {
  position: absolute;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255,255,255,0.2);
  color: white;
  font-size: 2rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-close:hover,
.lightbox-prev:hover,
.lightbox-next:hover {
  background: rgba(28, 196, 141, 0.8);
  transform: scale(1.1);
}

.lightbox-close {
  top: 20px;
  right: 20px;
}

.lightbox-prev {
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.lightbox-next {
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .gallery {
    padding: 3rem 1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .gallery-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .gallery-tabs {
    gap: 0.5rem;
  }

  .tab-button {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }

  .lightbox-close,
  .lightbox-prev,
  .lightbox-next {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
  }

  .lightbox-caption {
    font-size: 1rem;
  }
}
</style>